import { Button } from '@/components/ui/button'
import * as Card from '@/components/ui/card'
import * as Field from '@/components/ui/field'
import * as FileUpload from '@/components/ui/file-upload'
import { Heading } from '@/components/ui/heading'
import { IconButton } from '@/components/ui/icon-button'
import { Text } from '@/components/ui/text'
import { userId } from '@stateApp'
import { useQueryClient } from '@tanstack/solid-query'
import { Trash2Icon } from 'lucide-solid'
import { For, Show, createEffect, createResource, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { grid } from 'styled-system/patterns'
import { fetchImageUrl } from '../../../features/Avatar/fetchImageUrl'
import { useProfile } from '../state'
import { processAvatarToWebP } from './imageProcessing'

export const PictureUpload = () => {
  const [isValid, setIsValid] = createSignal(false)
  const [isCompressing, setIsCompressing] = createSignal(false)

  const queryClient = useQueryClient()

  const { profilePictureResource, uploadProfilePicture, setFileChange, fileChange } =
    useService(useProfile)()

  // Local preview resource
  const [imageUrl, { refetch }] = createResource(userId, fetchImageUrl, {
    initialValue: null
  })

  createEffect(() => {
    if (profilePictureResource.state === 'ready') {
      // 1. Invalidate global Avatar components powered by TanStack Query
      queryClient.invalidateQueries({ queryKey: ['avatarUrl', userId()] })

      // 2. Refetch the local preview in this component
      refetch()

      // 3. Hide the save button
      setFileChange(undefined)
    }
  })

  return (
    <Card.Root
      p={4}
      class={grid()}
    >
      <Heading>Image</Heading>
      <Text textStyle="xs">
        Upload any image up to 10MB. We'll automatically resize and optimize it for you.
      </Text>

      <img
        src={imageUrl() ?? undefined}
        alt=""
      />

      <FileUpload.Root
        maxFiles={1}
        accept="image/*"
        validate={(file) => {
          if (file.size > 10 * 1024 * 1024) {
            return ['FILE_TOO_LARGE']
          }

          if (!file.type.startsWith('image/')) {
            return ['FILE_INVALID_TYPE']
          }
          return null
        }}
        onFileChange={async (val) => {
          const file = val.acceptedFiles[0]
          if (!file) {
            setFileChange(undefined)
            return
          }

          try {
            setIsCompressing(true)
            const webpFile = await processAvatarToWebP(file, 256)
            setFileChange(webpFile)
          } catch (error) {
            console.error("Failed to compress image:", error)
          } finally {
            setIsCompressing(false)
          }
        }}
      >
        <Show when={!fileChange()}>
          <FileUpload.Dropzone minH="100px">
            <FileUpload.Trigger
              asChild={(triggerProps) => (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isCompressing() || profilePictureResource.loading}
                  {...triggerProps()}
                >
                  {isCompressing() ? 'Optimizing...' : 'Upload Profile Picture'}
                </Button>
              )}
            />
          </FileUpload.Dropzone>
        </Show>
        <FileUpload.ItemGroup>
          <FileUpload.Context>
            {(fileUpload) => (
              <For each={fileUpload().acceptedFiles}>
                {(file) => (
                  <FileUpload.Item file={file}>
                    <FileUpload.ItemPreview type="image/*">
                      <FileUpload.ItemPreviewImage />
                    </FileUpload.ItemPreview>
                    <FileUpload.ItemName />
                    <FileUpload.ItemSizeText />
                    <FileUpload.ItemDeleteTrigger
                      asChild={(triggerProps) => (
                        <IconButton
                          variant="plain"
                          size="sm"
                          {...triggerProps()}
                        >
                          <Trash2Icon />
                        </IconButton>
                      )}
                    />
                  </FileUpload.Item>
                )}
              </For>
            )}
          </FileUpload.Context>
        </FileUpload.ItemGroup>
        <FileUpload.HiddenInput />
      </FileUpload.Root>
      <Show when={fileChange()}>
        <Button
          loading={profilePictureResource.loading || isCompressing()}
          onClick={uploadProfilePicture}
          justifySelf="start"
          colorPalette="jade"
          variant="solid"
        >
          Save
        </Button>
      </Show>
    </Card.Root>
  )
}
